<template>
  <div class="sub-shell c-bg-ambient">
    <header v-show="!hideHeader" class="ss-header">
      <button
        v-if="!hideBack"
        class="ss-back"
        type="button"
        aria-label="ย้อนกลับ"
        @click="goBack"
      >
        <el-icon :size="22"><ArrowLeft /></el-icon>
      </button>

      <button
        class="ss-avatar"
        :style="avatarStyle"
        type="button"
        aria-label="โปรไฟล์ของฉัน"
        @click="goProfile"
      >
        <img
          v-if="liffProfile?.pictureUrl"
          :src="liffProfile.pictureUrl"
          :alt="liffProfile.displayName"
        />
        <span v-else class="ss-avatar-initial">{{ initial }}</span>
        <span class="ss-avatar-dot" title="เชื่อมต่อกับ LINE แล้ว" />
      </button>

      <div class="ss-greet">
        <div class="ss-greet-line">
          {{ greetingByTime }} <span class="ss-spark">☀️</span>
        </div>
        <div class="ss-name">
          {{ displayName }} <span class="ss-wave">👋</span>
        </div>
        <div class="ss-page-title">{{ title }}</div>
      </div>

      <div class="ss-actions">
        <slot name="actions" />
      </div>
    </header>

    <main class="ss-body">
      <slot />
    </main>

    <CustomerBottomNav v-if="!hideNav" :active="nav" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { ArrowLeft } from "@element-plus/icons-vue";
import CustomerBottomNav, { type CbnTab } from "./CustomerBottomNav.vue";
import { useLiff } from "@/composables/useLiff";
import { useCustomerMe } from "@/composables/useCustomerMe";

interface Props {
  title: string;
  fallback?: string;
  nav?: CbnTab;
  hideHeader?: boolean;
  hideBack?: boolean;
  hideNav?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  fallback: "/customer/home",
  nav: undefined,
  hideHeader: false,
  hideBack: false,
  hideNav: false,
});

const router = useRouter();
const { profile: liffProfile, init: initLiff, status: liffStatus } = useLiff();
const { me, load: loadMe } = useCustomerMe();

const greetingByTime = computed(() => {
  const h = new Date().getHours();
  if (h < 12) return "สวัสดีตอนเช้า";
  if (h < 17) return "สวัสดีตอนบ่าย";
  return "สวัสดีตอนเย็น";
});

const displayName = computed(() => {
  if (me.value?.fname || me.value?.lname) {
    return `คุณ${me.value.fname ?? ""} ${me.value.lname ?? ""}`.trim();
  }
  return liffProfile.value?.displayName ?? "ผู้ใช้";
});

const initial = computed(() => {
  const name = me.value?.fname ?? liffProfile.value?.displayName ?? "?";
  return [...name][0]?.toUpperCase() ?? "?";
});

const avatarStyle = computed(() => {
  const seed = liffProfile.value?.userId ?? "0";
  const hue = (seed.charCodeAt(2) ?? 200) % 360;
  return {
    background: `linear-gradient(135deg, hsl(${hue}, 70%, 65%), hsl(${(hue + 40) % 360}, 70%, 55%))`,
  };
});

function goBack(): void {
  if (window.history.length > 1) router.back();
  else router.replace(props.fallback);
}

function goProfile(): void {
  router.push({ name: "customer-profile" });
}

onMounted(async () => {
  if (liffStatus.value !== "ready") {
    await initLiff();
  }
  await loadMe();
});
</script>

<style scoped>
.sub-shell {
  /* Lock to viewport so header + bottom-nav stay fixed and only the body
     scrolls. Use dynamic viewport unit (dvh) so the chrome on iOS Safari /
     LINE in-app browser doesn't push the bottom nav off-screen. */
  height: 100vh;
  height: 100dvh;
  max-width: 440px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}
@media (min-width: 480px) {
  .sub-shell {
    box-shadow: 0 0 36px rgba(59, 130, 246, 0.1);
  }
}

/* ===== Header (home-style) ===== */
.ss-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: max(10px, env(safe-area-inset-top)) 12px 8px;
}
.ss-back {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  border: none;
  background: rgba(255, 255, 255, 0.6);
  color: var(--c-primary-ink);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.12s;
}
.ss-back:active {
  background: #dbeafe;
}

.ss-avatar {
  flex: 0 0 auto;
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
}
.ss-avatar:active {
  transform: scale(0.96);
}
.ss-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}
.ss-avatar-initial {
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}
.ss-avatar-dot {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #06c755;
  border: 2px solid #ffffff;
}

.ss-greet {
  flex: 1;
  min-width: 0;
}
.ss-greet-line {
  font-size: 11px;
  color: var(--c-ink-soft);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ss-spark {
  font-size: 12px;
}
.ss-name {
  font-size: 15px;
  font-weight: 800;
  color: var(--c-ink);
  margin-top: 1px;
  letter-spacing: -0.01em;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ss-wave {
  font-size: 13px;
  vertical-align: middle;
}
.ss-page-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--c-primary);
  margin-top: 2px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ss-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--c-primary-ink);
  flex: 0 0 auto;
}
.ss-actions :slotted(button),
.ss-actions :slotted(.ss-icon-btn) {
  background: rgba(255, 255, 255, 0.6);
  border: none;
  color: inherit;
  cursor: pointer;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.12s;
}
.ss-actions :slotted(button):active,
.ss-actions :slotted(.ss-icon-btn):active {
  background: #dbeafe;
}

.ss-body {
  flex: 1;
  /* min-height:0 is the critical bit — without it a flex child won't shrink
     below its content size, so the body would push the bottom nav off-screen
     instead of scrolling internally. */
  min-height: 0;
  padding: 0 0 80px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* Tiny screens — keep header from wrapping */
@media (max-width: 360px) {
  .ss-header {
    gap: 8px;
    padding-left: 10px;
    padding-right: 10px;
  }
  .ss-avatar {
    width: 36px;
    height: 36px;
  }
  .ss-name {
    font-size: 14px;
  }
}
</style>
